import { Component, ReactNode } from "react";
import { getRouteOwnershipContext } from "../../lib/routeOwnershipRegistry";

interface AppErrorBoundaryState {
  error: Error | null;
}

function getErrorTitle() {
  const pathname = typeof window === "undefined" ? "" : window.location.pathname;
  const errorBrand = getErrorBrandLabel(pathname);

  return `The ${errorBrand} could not connect.`;
}

function getErrorBrandLabel(pathname: string) {
  const normalizedPath = pathname || "/";

  // Memory Media Vault public and client-facing routes intentionally carry MMV branding even when
  // they live under shared public or client URL namespaces.
  if (
    normalizedPath.startsWith("/upload") ||
    normalizedPath.startsWith("/gallery/") ||
    normalizedPath.startsWith("/client/") ||
    (normalizedPath.startsWith("/memory-hub/") && normalizedPath !== "/memory-hub")
  ) {
    return "Memory Media Vault";
  }

  const route = getRouteOwnershipContext(normalizedPath);

  // Route-ownership groups are kept in the shared registry so shared platform pages, workspace pages,
  // and product pages can each expose the correct failure brand without duplicating route lists here.
  if (route.ownerName === "Truevance Connect") {
    return "Truevance Connect Business Center";
  }
  if (route.ownerName === "Kreatethedate") {
    return "Kreatethedate Forms Portal";
  }
  if (route.ownerName === "Memory Media Vault") {
    return "Memory Media Vault";
  }
  if (route.ownerName === "CollabRecover AR Solutions") {
    return "CollabRecover AR Solutions";
  }

  return "Business Center OS";
}

export class AppErrorBoundary extends Component<{ children: ReactNode }, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return { error };
  }

  render() {
    if (this.state.error) {
      return (
        <main className="grid min-h-screen place-items-center bg-ktd-black px-4 py-10 text-white">
          <section className="max-w-2xl rounded-lg border border-ktd-gold/25 bg-ktd-ivory p-8 text-ktd-rich shadow-luxe">
            <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-[#D94A16]">
              Backend Configuration Required
            </p>
            <h1 className="mt-4 font-display text-3xl font-bold">{getErrorTitle()}</h1>
            <p className="mt-4 text-sm font-semibold leading-6 text-ktd-muted">{this.state.error.message}</p>
          </section>
        </main>
      );
    }

    return this.props.children;
  }
}
